export interface OrderedSection {
  id: string;
  position: number;
}

export interface OrderedQuestion {
  id: string;
  sectionId: string;
  position: number;
}

// The server orders both lists with `acts_as_list scope: :questionnaire_id`,
// so question positions are survey-wide, not per section. Each drag therefore
// writes ONE row: the dragged row's new position. acts_as_list shifts the rest
// without changing their relative order. Several PATCHes in a row would each
// shift rows in other sections and scramble them.

// Mirrors acts_as_list 1.2.4 update_positions (list.rb:404-414 + 341-382).
const simulateMove = <T extends { id: string; position: number }>(
  rows: T[],
  id: string,
  to: number
): T[] => {
  const moving = rows.find((r) => r.id === id);
  if (!moving || moving.position === to) return rows;
  const from = moving.position;
  const taken = rows.some((r) => r.id !== id && r.position === to);
  return rows.map((r) => {
    if (r.id === id) return { ...r, position: to };
    if (!taken) return r;
    if (to < from && r.position >= to && r.position < from)
      return { ...r, position: r.position + 1 };
    if (to > from && r.position > from && r.position <= to)
      return { ...r, position: r.position - 1 };
    return r;
  });
};

// Position that lands the moving row directly before/after the row at `anchor`.
const slotBeside = (from: number, anchor: number, side: "before" | "after") =>
  side === "before"
    ? from > anchor
      ? anchor
      : anchor - 1
    : from < anchor
    ? anchor
    : anchor + 1;

// Move `activeId` to where `overId` sits. Returns the list in display order and
// the single row to PATCH. Does not depend on the input array's order.
export const reorderSections = <T extends OrderedSection>(
  sections: T[],
  activeId: string,
  overId: string
): { next: T[]; changed: T[] } => {
  const active = sections.find((s) => s.id === activeId);
  const over = sections.find((s) => s.id === overId);
  if (!active || !over || active.position === over.position)
    return { next: sections, changed: [] };
  const next = simulateMove(sections, activeId, over.position).sort(
    (a, b) => a.position - b.position
  );
  const moved = next.find((s) => s.id === activeId) as T;
  return { next, changed: [moved] };
};

// Apply a question drag. `overIsSection` true when dropped on a section
// container (overId is the section id); otherwise overId is a question id.
// `sectionOrder` is the section ids in displayed order. Returns the predicted
// rows and the single row to PATCH (position and section).
export const applyQuestionDrag = <T extends OrderedQuestion>(
  questions: T[],
  activeId: string,
  overId: string,
  overIsSection: boolean,
  sectionOrder: string[]
): { next: T[]; changed: T[] } => {
  const active = questions.find((q) => q.id === activeId);
  if (!active) return { next: questions, changed: [] };

  let sectionId: string;
  let to: number;

  if (overIsSection) {
    if (active.sectionId === overId) return { next: questions, changed: [] };
    sectionId = overId;
    // Append after the target section's last visible question.
    const last = questions
      .filter((q) => q.sectionId === overId)
      .reduce<T | undefined>(
        (max, q) => (!max || q.position > max.position ? q : max),
        undefined
      );
    to = last
      ? slotBeside(active.position, last.position, "after")
      : active.position;
  } else {
    const over = questions.find((q) => q.id === overId);
    if (!over || over.id === active.id) return { next: questions, changed: [] };
    sectionId = over.sectionId;
    // Dragged up (active shown below `over`) lands before it; down, after.
    const activeIdx = sectionOrder.indexOf(active.sectionId);
    const overIdx = sectionOrder.indexOf(over.sectionId);
    const shownBelow =
      activeIdx !== overIdx
        ? activeIdx > overIdx
        : active.position > over.position;
    to = slotBeside(
      active.position,
      over.position,
      shownBelow ? "before" : "after"
    );
  }

  if (to === active.position && sectionId === active.sectionId)
    return { next: questions, changed: [] };

  const next = simulateMove(questions, activeId, to).map((q) =>
    q.id === activeId ? { ...q, sectionId } : q
  );
  const moved = next.find((q) => q.id === activeId) as T;
  return { next, changed: [moved] };
};
